import { SessionType } from '@shared';
import { useSelector } from 'react-redux';
import { HrefLink, PageLink, WizardAdvisorHierarchy, cn } from '..';
import { selectSession } from '../model/selectors';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WizardAdvisorAdminPortal } from '@/pages/WizardAdvisorAdminPortal';
import { STRIPE_BILLING_URL } from '@/util/stripe/urls';
import { CreditCard, UserCircle, UserPen, WorkflowIcon } from 'lucide-react';
import { MdLogout } from 'react-icons/md';
import AvatarMenuButton from './UserAvatarButtonContent';

interface AdvisorUserMenuProps {
  handleClickSignOut: () => void;
}

export function AdvisorUserMenu({ handleClickSignOut }: AdvisorUserMenuProps) {
  const { sessionType, advisor } = useSelector(selectSession);
  const buttonStyle =
    'group w-full flex gap-x-3 rounded-md p-2 text-sm font-medium leading-6 text-gray-800 hover:bg-primary-50';

  const billingLink = (
    <a
      className={buttonStyle}
      href={STRIPE_BILLING_URL}
      target="_blank"
      rel="noopener noreferrer"
      id="menu-billing"
      data-testid="menu-billing"
    >
      <CreditCard
        className={cn('text-gray-800', 'h-6 w-6 shrink-0')}
        aria-hidden="true"
      />
      Billing
    </a>
  );
  const signOutButton = (
    <button
      className={buttonStyle}
      onClick={handleClickSignOut}
      id="menu-signout"
    >
      <MdLogout
        className={cn('text-gray-800 ', 'h-6 w-6 shrink-0')}
        aria-hidden="true"
      />
      Sign Out
    </button>
  );
  const advisorHierarchyLink = (
    <PageLink
      className={buttonStyle}
      to={WizardAdvisorHierarchy}
      id="menu-advisor-hierarchy"
    >
      <WorkflowIcon
        className={cn('text-gray-800', 'h-6 w-6 shrink-0')}
        aria-hidden="true"
      />
      Advisor Hierarchy
    </PageLink>
  );
  const advisorAdminPortalLink = (
    <PageLink
      className={buttonStyle}
      to={WizardAdvisorAdminPortal}
      id="menu-advisor-admin-portal"
    >
      <UserPen
        className={cn('text-gray-800', 'h-6 w-6 shrink-0')}
        aria-hidden="true"
      />
      Advisor Admin Portal
    </PageLink>
  );
  const advisorProfileLink = (
    <HrefLink
      className={buttonStyle}
      to={`/advisors/${advisor?.advisorId}/settings`}
      id="menu-profile"
      data-testid="menu-profile"
    >
      <UserCircle
        className={cn('text-gray-800', 'h-6 w-6 shrink-0')}
        aria-hidden="true"
      />
      Profile
    </HrefLink>
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={`hover:border-primary-300 hover:bg-primary-50 flex items-center gap-2 whitespace-nowrap rounded-full border border-white bg-white px-2 py-1 pl-1 shadow-sm`}
        id="profile-menu-trigger"
        data-testid="profile-menu-trigger"
      >
        <AvatarMenuButton
          avatarFallback={advisor?.advisorFirstName?.[0] ?? ''}
          buttonText={`${advisor?.advisorFirstName} ${advisor?.advisorLastName}`}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-64 rounded-xl p-3"
        align="start"
        side="top"
        sideOffset={10}
      >
        <DropdownMenuItem asChild>{advisorProfileLink}</DropdownMenuItem>
        <DropdownMenuItem asChild>{billingLink}</DropdownMenuItem>
        {sessionType === SessionType.wizard && (
          <>
            <DropdownMenuItem asChild>{advisorHierarchyLink}</DropdownMenuItem>
            <DropdownMenuItem asChild>{advisorAdminPortalLink}</DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem asChild>{signOutButton}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
